import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { INFINITE_PAGE_SIZE, ReadOnlyItem, Row } from '@ydistri/ds';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../store';
import { GetProductListProductsPayload, useGetProductListProductsQuery } from '../apiProductLists';
import { skipToken } from '@reduxjs/toolkit/query';
import EditIconButton from '../../../../components/buttons/EditIconButton';
import ProductCatalog from '../../../../components/global/ProductCatalog/ProductCatalog';
import { EntityListSourceType } from '@ydistri/api-sdk';
import { useStableParams } from '../../../../routes/useStableParams';
import EntityListItemsSectionHeader from '../../../ProjectAdministration/EntityListAdministration/content/detail/EntityListItemsSectionHeader';
import { SYSTEM_ID_LABEL } from '../../../../components/global/_constants';
import { useClipboardHandlers } from '../../../../lib/hooks/useClipboardHandlers';
import { DATA_DELETED_VERSION_TOOLTIP } from '../../../../components/domain/EntityLists/DataDeletedEntityListIcon';

export type ProductListDetailParams = GetProductListProductsPayload;

const ProductListDetail: React.FC = () => {
  const { projectShortName } = useStableParams();
  const clipboardHandlers = useClipboardHandlers();

  const selectedProductListConfiguration = useSelector(
    (state: ReduxState) => state.productListConfiguration.selectedProductListConfiguration,
  );

  const [params, setParams] = useState<ProductListDetailParams>({
    productListId: selectedProductListConfiguration?.productListId ?? 0,
    entityListVersionId: selectedProductListConfiguration?.productListVersionId,
    instance: `${selectedProductListConfiguration?.productListId ?? 0}:${
      selectedProductListConfiguration?.productListVersionId ?? 'latest'
    }`,
    skip: 0,
    top: INFINITE_PAGE_SIZE,
  });

  // A version whose data were deleted has no products to ask for.
  const dataDeleted = selectedProductListConfiguration?.isDataDeleted ?? false;
  const { data: productListProducts, isFetching } = useGetProductListProductsQuery(
    params.productListId > 0 && !dataDeleted ? params : skipToken,
  );

  useEffect(() => {
    if (selectedProductListConfiguration?.productListId) {
      setParams(p => ({
        ...p,
        productListId: selectedProductListConfiguration.productListId,
        entityListVersionId: selectedProductListConfiguration.productListVersionId,
        instance: `${selectedProductListConfiguration.productListId}:${selectedProductListConfiguration.productListVersionId}`,
        skip: 0,
      }));
    }
  }, [
    selectedProductListConfiguration?.productListId,
    selectedProductListConfiguration?.productListVersionId,
  ]);

  /* ========================= Header ========================= */

  const header = useMemo(() => {
    if (selectedProductListConfiguration) {
      return (
        <EntityListItemsSectionHeader
          title={selectedProductListConfiguration.name}
          entityListSourceType={selectedProductListConfiguration.productListSourceTypeId}
          archived={selectedProductListConfiguration.isArchived}
          dataDeleted={selectedProductListConfiguration.isDataDeleted ? 'version' : undefined}
        />
      );
    } else {
      return 'Product List Detail';
    }
  }, [selectedProductListConfiguration]);

  const onEdit = useCallback(() => {
    if (projectShortName && selectedProductListConfiguration?.productListId) {
      window.open(
        `/${projectShortName}/project-administration/productlists/${selectedProductListConfiguration.productListId}`,
      );
    }
  }, [projectShortName, selectedProductListConfiguration]);

  /* ========================= Header actions ========================= */

  const headerActions = useMemo(() => {
    const isEditable =
      selectedProductListConfiguration?.productListSourceTypeId === EntityListSourceType.User;

    return (
      <Row $gap="1rem">
        <ReadOnlyItem
          label="Type"
          value={selectedProductListConfiguration?.productListSourceTypeId}
        />
        <EditIconButton
          size="small"
          tooltip="Edit product list in administration"
          tooltipPosition="top"
          onClick={onEdit}
          data-testid="EditProductList"
          disabled={!isEditable}
        />
      </Row>
    );
  }, [onEdit, selectedProductListConfiguration]);

  const summary = useMemo(() => {
    if (!selectedProductListConfiguration) return undefined;

    const data = [
      {
        label: 'Id',
        value: selectedProductListConfiguration.customerProductListId,
      },
      {
        label: SYSTEM_ID_LABEL,
        value: selectedProductListConfiguration.productListId,
      },
      { label: 'Version Id', value: selectedProductListConfiguration.productListVersionId },
    ];

    return (
      <Row $gap="1rem">
        {data.map(item => (
          <ReadOnlyItem
            key={item.label}
            label={item.label}
            value={item.value}
            copyOnClick
            {...clipboardHandlers}
          />
        ))}
      </Row>
    );
  }, [clipboardHandlers, selectedProductListConfiguration]);

  let alertMessage: string | undefined;
  if (!selectedProductListConfiguration) {
    alertMessage = 'No selected product list';
  } else if (dataDeleted) {
    alertMessage = DATA_DELETED_VERSION_TOOLTIP;
  }

  return (
    <ProductCatalog
      header={header}
      headerActions={headerActions}
      id="product-list-detail"
      data={dataDeleted ? undefined : productListProducts}
      loading={isFetching}
      setParams={setParams}
      alertMessage={alertMessage}
      alertDescription={
        !selectedProductListConfiguration ? 'Select a product list to view its products' : undefined
      }
      summary={summary}
    />
  );
};

export default ProductListDetail;
